import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import * as unifiedApi from '../../services/unifiedApi';
import { Formulation, FormulationSummary } from '../../types/formulation';

interface Target {
  element: string;
  value: number;
  unit: string;
}

// Async thunk to fetch all formulations (using unified API)
export const fetchFormulations = createAsyncThunk(
  'formulation/fetchFormulations',
  async (_, { rejectWithValue }) => {
    try {
      const payload = await unifiedApi.getFormulations();
      // The API wraps lists as { data: [...] }; older callers received the bare array.
      return (Array.isArray(payload) ? payload : payload?.data ?? []) as FormulationSummary[];
    } catch (error) {
      console.error('Failed to fetch formulations:', error);
      return rejectWithValue('Failed to fetch formulations');
    }
  }
);

// Async thunk to search formulations (using unified API)
export const searchFormulations = createAsyncThunk(
  'formulation/searchFormulations',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      return await unifiedApi.searchFormulations(searchTerm);
    } catch (error) {
      console.error('Failed to search formulations:', error);
      return rejectWithValue('Failed to search formulations');
    }
  }
);

// Async thunk to fetch formulation by ID (using unified API)
export const fetchFormulationById = createAsyncThunk(
  'formulation/fetchFormulationById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await unifiedApi.getFormulationById(id);
    } catch (error) {
      console.error('Failed to fetch formulation:', error);
      return rejectWithValue('Failed to fetch formulation');
    }
  }
);

// Async thunk to create a new formulation (using unified API)
export const createFormulation = createAsyncThunk(
  'formulation/createFormulation',
  async (formulation: Omit<Formulation, 'id'>, { rejectWithValue }) => {
    try {
      return await unifiedApi.createFormulation(formulation);
    } catch (error) {
      console.error('Failed to create formulation:', error);
      return rejectWithValue('Failed to create formulation');
    }
  }
);

// Async thunk to update a formulation (using unified API)
export const updateFormulation = createAsyncThunk(
  'formulation/updateFormulation',
  async (formulation: Formulation, { rejectWithValue }) => {
    try {
      return await unifiedApi.updateFormulation(formulation.id!, formulation);
    } catch (error) {
      console.error(`Failed to update formulation with id ${formulation.id}:`, error);
      return rejectWithValue(`Failed to update formulation with id ${formulation.id}`);
    }
  }
);

// Async thunk to delete a formulation (using unified API)
export const deleteFormulation = createAsyncThunk(
  'formulation/deleteFormulation',
  async (id: string, { rejectWithValue }) => {
    try {
      await unifiedApi.deleteFormulation(id);
      return id;
    } catch (error) {
      console.error(`Failed to delete formulation with id ${id}:`, error);
      return rejectWithValue(`Failed to delete formulation with id ${id}`);
    }
  }
);

interface FormulationState {
  name: string;
  targets: Target[];
  loading: boolean;
  error: string | null;
  selectedFormulation: Formulation | null;
  selectedFormulationId: string | null;
  formulations: FormulationSummary[];
  creatingFormulation: boolean;
  updatingFormulation: boolean;
  deletingFormulation: boolean;
}

const initialState: FormulationState = {
  name: 'New Formulation',
  targets: [],
  loading: false,
  error: null,
  selectedFormulation: null,
  selectedFormulationId: null,
  formulations: [],
  creatingFormulation: false,
  updatingFormulation: false,
  deletingFormulation: false,
};

const formulationSlice = createSlice({
  name: 'formulation',
  initialState,
  reducers: {
    addTarget(state, action: PayloadAction<Target>) {
      state.targets.push(action.payload);
    },
    updateTarget(state, action: PayloadAction<{ index: number; target: Target }>) {
      const { index, target } = action.payload;
      if (index >= 0 && index < state.targets.length) {
        state.targets[index] = target;
      }
    },
    removeTarget(state, action: PayloadAction<number>) {
      state.targets = state.targets.filter((_, index) => index !== action.payload);
    },
    setFormulationName(state, action: PayloadAction<string>) {
      state.name = action.payload;
    },
    setSelectedFormulationId(state, action: PayloadAction<string>) {
      state.selectedFormulationId = action.payload;
    },
    clearSelectedFormulation(state) {
      state.selectedFormulation = null;
      state.selectedFormulationId = null;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchFormulations async thunk
    builder.addCase(fetchFormulations.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchFormulations.fulfilled, (state, action: PayloadAction<FormulationSummary[]>) => {
      state.loading = false;
      state.formulations = action.payload;
    });
    builder.addCase(fetchFormulations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Handle fetchFormulationById async thunk
    builder.addCase(fetchFormulationById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });    builder.addCase(fetchFormulationById.fulfilled, (state, action: PayloadAction<Formulation>) => {
      state.loading = false;
      state.selectedFormulation = action.payload;
      state.selectedFormulationId = action.payload.id || null;
    });
    builder.addCase(fetchFormulationById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Handle createFormulation async thunk
    builder.addCase(createFormulation.pending, (state) => {
      state.creatingFormulation = true;
      state.error = null;
    });
    builder.addCase(createFormulation.fulfilled, (state, action: PayloadAction<Formulation>) => {
      state.creatingFormulation = false;
      state.formulations.push({
        id: action.payload.id || '',
        name: action.payload.name,
        description: action.payload.description,
        targetEc: action.payload.targetEc,
        targetPh: action.payload.targetPh,
        elementCount: action.payload.elements.length,
        createdAt: action.payload.createdAt
      });
    });
    builder.addCase(createFormulation.rejected, (state, action) => {
      state.creatingFormulation = false;
      state.error = action.payload as string;
    });

    // Handle updateFormulation async thunk
    builder.addCase(updateFormulation.pending, (state) => {
      state.updatingFormulation = true;
      state.error = null;
    });
    builder.addCase(updateFormulation.fulfilled, (state, action: PayloadAction<Formulation>) => {
      state.updatingFormulation = false;
      // Update the formulation in the list
      const index = state.formulations.findIndex(f => f.id === action.payload.id);
      if (index >= 0) {
        state.formulations[index] = {
          id: action.payload.id || '',
          name: action.payload.name,
          description: action.payload.description,
          targetEc: action.payload.targetEc,
          targetPh: action.payload.targetPh,
          elementCount: action.payload.elements.length,
          createdAt: action.payload.createdAt
        };
      }
      // Update selected formulation if it's the same one
      if (state.selectedFormulationId === action.payload.id) {
        state.selectedFormulation = action.payload;
      }
    });
    builder.addCase(updateFormulation.rejected, (state, action) => {
      state.updatingFormulation = false;
      state.error = action.payload as string;
    });

    // Handle deleteFormulation async thunk
    builder.addCase(deleteFormulation.pending, (state) => {
      state.deletingFormulation = true;
      state.error = null;
    });
    builder.addCase(deleteFormulation.fulfilled, (state, action: PayloadAction<string>) => {
      state.deletingFormulation = false;
      state.formulations = state.formulations.filter(f => f.id !== action.payload);
      if (state.selectedFormulationId === action.payload) {
        state.selectedFormulation = null;
        state.selectedFormulationId = null;
      }
    });
    builder.addCase(deleteFormulation.rejected, (state, action) => {
      state.deletingFormulation = false;
      state.error = action.payload as string;
    });
  },
});

export const {
  addTarget,
  updateTarget,
  removeTarget,
  setFormulationName,
  setSelectedFormulationId,
  clearSelectedFormulation,
} = formulationSlice.actions;

export default formulationSlice.reducer;
